import { toastMsgError } from "@/core/helpers/toastMessage";
import ApiService from "@/core/services/ApiService";
import JwtService from "@/core/services/JwtService";
import { Actions, Mutations } from "@/store/enums/StoreEnums";
import { Module, Action, Mutation, VuexModule } from "vuex-module-decorators";

export interface User {
  first_name: string;
  last_name: string;
  phone_number: string;
  email: string;
  password: string;
  api_token: string;
}

export interface UserAuthInfo {
  errors: unknown;
  user: User;
  isAuthenticated: boolean;
}

@Module
export default class AuthModule extends VuexModule implements UserAuthInfo {
  errors = {};
  user = {} as User;
  isAuthenticated = !!JwtService.getToken();
  mentor = {};
  errorsAll = {};

  /**
   * Get current user object
   * @returns User
   */
  get currentUser(): User {
    return this.user;
  }

  /**
   * Verify user authentication
   * @returns boolean
   */
  get isUserAuthenticated(): boolean {
    return this.isAuthenticated;
  }

  /**
   * Get authentification errors
   * @returns array
   */
  get getErrors() {
    return this.errors;
  }

  @Mutation
  [Mutations.SET_ERROR](error) {
    this.errors = { ...error };
  }

  @Mutation
  [Mutations.SET_AUTH](user) {
    this.isAuthenticated = true;
    this.user = user;
    this.errors = {};
    this.errorsAll = {};
    JwtService.saveToken(user.data.token);
  }

  @Mutation
  [Mutations.SET_USER](user) {
    this.user = user;
  }

  @Mutation
  [Mutations.SET_PASSWORD](password) {
    this.user.password = password;
  }

  @Mutation
  [Mutations.PURGE_AUTH]() {
    this.isAuthenticated = false;
    this.user = {} as User;
    this.errors = [];
    this.errorsAll = {};
    JwtService.destroyToken();
  }

  @Mutation
  [Mutations.PURE_AUTH_AND_SET_ERROR](responseData) {
    if (responseData.code == 401) {
      this.isAuthenticated = false;
      this.user = {} as User;
      this.errors = [];
      this.errorsAll = {};
      JwtService.destroyToken();
    } else {
      let error = {};
      if (responseData.code == 403) {
        toastMsgError(responseData.message);
      }
      error = {
        errors: {
          errors_mess: [responseData.message],
        },
      };
      this.errors = { ...error };
      this.errorsAll = { ...responseData };
    }
  }

  @Action
  [Actions.LOGIN](credentials) {
    return ApiService.post("/login", credentials)
      .then(({ data }) => {
        localStorage.setItem("userInfo", JSON.stringify(data.data.manager));
        this.context.commit(Mutations.SET_AUTH, data);
        this.context.commit(Mutations.SET_USER, data);
      })
      .catch(({ response }) => {
        const error = {
          errors: {
            errors_mess: [response.data.message],
          },
        };
        this.context.commit(Mutations.SET_ERROR, error);
      });
  }

  @Action
  [Actions.LOGOUT]() {
    if (JwtService.getToken()) {
      ApiService.setHeader();
      return ApiService.getData("/logout")
        .then(({ data }) => {
          this.context.commit(Mutations.PURGE_AUTH);
        })
        .catch(({ response }) => {
          const error = {
            errors: {
              errors_mess: [response.data.message],
            },
          };
          this.context.commit(Mutations.SET_ERROR, error);
        });
    } else {
      this.context.commit(Mutations.PURGE_AUTH);
    }
  }

  @Action
  [Actions.REGISTER](credentials) {
    return ApiService.post("/register", credentials)
      .then(({ data }) => {
        // this.context.commit(Mutations.SET_AUTH, data);
      })
      .catch(({ response }) => {
        const error = {
          errors: {
            errors_mess: [response.data.message],
          },
        };
        this.context.commit(Mutations.SET_ERROR, error);
      });
  }

  @Action
  [Actions.FORGOT_PASSWORD](payload) {
    return ApiService.post("forgot_password", payload)
      .then(() => {
        this.context.commit(Mutations.SET_ERROR, {});
      })
      .catch(({ response }) => {
        const error = {
          errors: {
            errors_mess: [response.data.message],
          },
        };
        this.context.commit(Mutations.SET_ERROR, error);
      });
  }

  @Action
  [Actions.VERIFY_AUTH](payload) {
    if (JwtService.getToken()) {
      ApiService.setHeader();
      ApiService.post("verify_token", payload)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_USER, data);
          this.context.commit(Mutations.SET_ERROR, {});
        })
        .catch(({ response }) => {
          this.context.commit(
            Mutations.SET_ERROR,
            response?.data?.errors || {}
          );
          this.context.commit(Mutations.PURGE_AUTH);
        });
    } else {
      this.context.commit(Mutations.PURGE_AUTH);
    }
  }
}
